import React, { useCallback, useEffect, useState } from 'react';
import { LuClipboardCheck, LuLoader } from 'react-icons/lu';
import { importKicker, importSubtitle } from '../../utils/importSourceLabels';

export default function LearnImportReviewPanel({
  job,
  apiClient,
  onConfirmed,
  onDiscarded,
  onError,
}) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [discardBusy, setDiscardBusy] = useState(false);
  const [expanded, setExpanded] = useState({});

  const loadPreview = useCallback(async () => {
    if (!job?.id || !apiClient) return;
    setLoading(true);
    try {
      const res = await apiClient.get(`/api/v1/learn/imports/${job.id}/preview`);
      setPreview(res.data);
    } catch (err) {
      onError?.(err?.response?.data?.message || 'Could not load generated questions for review.');
    } finally {
      setLoading(false);
    }
  }, [apiClient, job?.id, onError]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  const toggleExpanded = (index) => {
    setExpanded((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const confirm = async () => {
    if (!job?.id || confirmBusy) return;
    setConfirmBusy(true);
    try {
      const res = await apiClient.post(`/api/v1/learn/imports/${job.id}/confirm`);
      onConfirmed?.(res.data);
    } catch (err) {
      onError?.(err?.response?.data?.message || 'Could not save questions to this notebook.');
    } finally {
      setConfirmBusy(false);
    }
  };

  const discard = async () => {
    if (!job?.id || discardBusy) return;
    const ok = window.confirm(
      'Discard these generated questions?\n\nNothing will be added to your notebook.',
    );
    if (!ok) return;
    setDiscardBusy(true);
    try {
      const res = await apiClient.post(`/api/v1/learn/imports/${job.id}/discard`);
      onDiscarded?.(res.data);
    } catch (err) {
      onError?.(err?.response?.data?.message || 'Could not discard this import.');
    } finally {
      setDiscardBusy(false);
    }
  };

  const count = preview?.questionCount ?? job?.questionsGenerated ?? 0;
  const items = preview?.questions || [];

  return (
    <div className="learn-import-panel learn-import-panel--review" aria-live="polite">
      <div className="learn-import-head">
        <span className="learn-import-icon learn-import-icon--active" aria-hidden>
          <LuClipboardCheck />
        </span>
        <div className="learn-import-head-text">
          <p className="learn-import-kicker">{importKicker(job)}</p>
          <p className="learn-import-title">Review generated questions</p>
          <p className="learn-import-sub">
            {count} question{count === 1 ? '' : 's'} ready. Confirm to add them to this notebook, or discard to start over.
          </p>
          <p className="learn-import-meta">{importSubtitle(job)}</p>
        </div>
      </div>

      {loading && (
        <p className="learn-import-review-loading">
          <LuLoader className="learn-import-spinner" aria-hidden />
          Loading preview…
        </p>
      )}

      {!loading && items.length > 0 && (
        <ol className="learn-import-review-list">
          {items.map((item, index) => {
            const open = Boolean(expanded[index]);
            return (
              <li key={`${index}-${String(item.question || '').slice(0, 24)}`} className="learn-import-review-item">
                <button
                  type="button"
                  className="learn-import-review-q"
                  onClick={() => toggleExpanded(index)}
                  aria-expanded={open}
                >
                  <span className="learn-import-review-num">{index + 1}</span>
                  <span className="learn-import-review-q-text">{item.question}</span>
                </button>
                {open && (
                  <div className="learn-import-review-a">
                    <p className="learn-import-review-a-label">Answer</p>
                    <p>{item.answer}</p>
                    {item.extraInfo && (
                      <p className="learn-import-review-extra">{item.extraInfo}</p>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}

      {!loading && items.length === 0 && (
        <p className="learn-muted learn-import-review-empty">No preview available for this import.</p>
      )}

      <div className="learn-import-actions learn-import-review-actions">
        <button
          type="button"
          className="home-btn home-btn-primary learn-btn--compact"
          onClick={confirm}
          disabled={loading || confirmBusy || discardBusy || items.length === 0}
        >
          {confirmBusy ? 'Saving…' : `Add ${count} to notebook`}
        </button>
        <button
          type="button"
          className="home-btn home-btn-secondary learn-btn--compact"
          onClick={discard}
          disabled={confirmBusy || discardBusy}
        >
          {discardBusy ? 'Discarding…' : 'Discard'}
        </button>
      </div>
    </div>
  );
}
